/* global NDEFReader */
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { WalletContext } from "../context/WalletContext";

export default function LandingPage() {
  const { selectedWallet, connectWallet, isConnecting } = useContext(WalletContext);
  const [reading, setReading] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showManualTopup, setShowManualTopup] = useState(false);
  const [manualCardData, setManualCardData] = useState("");
  const [manualError, setManualError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasNFC, setHasNFC] = useState(false);
  const navigate = useNavigate();

  // Check NFC support on mount
  useEffect(() => {
    const checkNFCSupport = () => {
      const isNFCSupported = "NDEFReader" in window;
      setHasNFC(isNFCSupported);
      console.log("NFC Support:", isNFCSupported);
    };

    checkNFCSupport();
  }, []);

  const handleConnectMetaMask = async () => {
    await connectWallet(false);
  };

  const handleConnectWalletConnect = async () => {
    await connectWallet(true);
  };

  const handleRegisterCard = () => {
    if (!selectedWallet) return alert("Please connect your wallet first!");
    navigate("/register-card");
  };

  // Parse card data for payment (expects: cardId|share2)
  const parsePaymentCardData = (input) => {
    try {
      const cleaned = input.trim();
      const [cardId, share2] = cleaned.split('|');
      
      if (!cardId || !share2) {
        throw new Error("Invalid format. Expected: cardId|share2");
      }
      
      return { cardId: cardId.trim(), share2: share2.trim() };
    } catch (err) {
      throw err;
    }
  };

  const handleManualPaymentEntry = () => {
    if (!selectedWallet) {
      alert("Please connect your wallet first!");
      return;
    }

    setManualError("");

    try {
      const { cardId, share2 } = parsePaymentCardData(manualCardData);
      navigate("/amount", {
        state: { 
          cardId, 
          share2,
          merchantAddress: selectedWallet
        },
      });
    } catch (err) {
      setManualError(err.message);
    }
  };

  // Handle manual topup
  const handleManualTopupEntry = async () => {
    if (!selectedWallet) {
      alert("Please connect your wallet first!");
      return;
    }

    setManualError("");
    setLoading(true);

    try {
      let cardId = manualCardData.trim();
      
      console.log("Original input:", cardId);
      
      // Extract cardId if user pasted full data (cardId|anything)
      if (cardId.includes('|')) {
        cardId = cardId.split('|')[0].trim();
        console.log("Extracted cardId from pipe:", cardId);
      }
      
      console.log("Final cardId to fetch:", cardId);
      
      // Fetch card details from API
      const apiUrl = `https://bitspend-api.onrender.com/api/cards/${cardId}`;
      console.log("Fetching from:", apiUrl);
      
      const response = await fetch(apiUrl);
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`Card not found. Please check the Card ID.`);
      }
      
      const data = await response.json();
      console.log("API response data:", data);
      
      if (!data.success) {
        throw new Error(data.error || "Failed to fetch card details");
      }
      
      const cardAddress = data.data.cardAddress;
      
      console.log("✅ Card found:", cardId, cardAddress);
      
      // Navigate to topup
      navigate("/topup", {
        state: { 
          cardId, 
          cardAddress 
        },
      });
    } catch (err) {
      console.error("❌ Manual topup error:", err);
      setManualError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasteCardData = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setManualCardData(text);
      setManualError("");
    } catch (err) {
      setManualError("Failed to read from clipboard");
    }
  };

  const handleReadNfc = async () => {
    if (!selectedWallet) return alert("Please connect your wallet first!");

    try {
      setReading(true);
      const ndef = new window.NDEFReader();
      await ndef.scan();

      ndef.onreadingerror = () => {
        setReading(false);
        alert("Unable to read NFC card. Try again.");
      };

      ndef.onreading = (event) => {
        try {
          const record = event.message.records[0];
          const decoder = new TextDecoder("utf-8");
          const data =
            record.data instanceof DataView ? record.data.buffer : record.data;
          const text = decoder.decode(data).trim();
          const [cardId, share2] = text.split("|");

          if (!cardId || !share2) throw new Error("Invalid format");

          setReading(false);

          navigate("/amount", {
            state: { 
              cardId, 
              share2,
              merchantAddress: selectedWallet
            },
          });
        } catch (err) {
          setReading(false);
          alert("Unable to read NFC card.");
        }
      };
    } catch (err) {
      setReading(false);
      alert("NFC scanning failed or permission denied.");
    }
  };

  const handleTopUp = async () => {
    if (!selectedWallet) return alert("Please connect your wallet first!");

    try {
      const ndef = new NDEFReader();
      await ndef.scan();
      alert("📶 Tap your card to the back of your phone...");

      ndef.onreading = (event) => {
        const decoder = new TextDecoder();
        let rawData = "";
        for (const record of event.message.records) {
          rawData += decoder.decode(record.data);
        }

        const [cardId, cardAddress] = rawData.split("|");

        if (!cardId || !cardAddress) {
          alert("Invalid card data on NFC tag.");
          return;
        }

        navigate("/topup", { state: { cardId, cardAddress } });
      };
    } catch (error) {
      console.error("NFC scan error:", error);
      alert("Failed to read NFC card.");
    }
  };

  // Smart handler - NFC if available, otherwise manual
  const handleSmartPayment = () => {
    if (hasNFC) {
      handleReadNfc();
    } else {
      setShowManualPayment(true);
      setManualCardData("");
      setManualError("");
    }
  };

  const handleSmartTopup = () => {
    if (hasNFC) {
      handleTopUp();
    } else {
      setShowManualTopup(true);
      setManualCardData("");
      setManualError("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-black to-gray-900 text-white p-6">
      <Header />

      {/* Hero Section */}
      <div className="flex flex-col items-center text-center mt-20 flex-1">
        <h2 className="text-4xl font-extrabold text-blue-300 drop-shadow-md">
          Spend MUSD. With Ease.
        </h2>
        <p className="text-gray-300 mt-4 max-w-sm">
          BitSpend lets you use your MUSD seamlessly for real-world payments.
          Secure. Fast. Global.
        </p>

        {/* Wallet Connect Buttons */}
        {!selectedWallet && !isConnecting && (
          <div className="flex flex-col gap-3 mt-8 w-full max-w-sm">
            <button
              onClick={handleConnectMetaMask}
              className="w-full px-6 py-3 rounded-xl text-lg font-bold shadow-lg bg-orange-600 hover:bg-orange-700 flex items-center justify-center gap-3"
            >
              <span className="text-2xl">🦊</span>
              MetaMask
            </button>
            <button
              onClick={handleConnectWalletConnect}
              className="w-full px-6 py-3 rounded-xl text-lg font-bold shadow-lg bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-3"
            >
              <span className="text-2xl">🔗</span>
              WalletConnect
            </button>
          </div>
        )}

        {isConnecting && (
          <div className="mt-6 bg-blue-500/20 border border-blue-500 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
              <p className="text-blue-200 text-sm">Connecting wallet...</p>
            </div>
          </div>
        )}

        {/* After Wallet Connected */}
        {selectedWallet && !showManualPayment && !showManualTopup && (
          <div className="mt-10 flex flex-col gap-4 w-full max-w-sm">
            <button
              onClick={handleRegisterCard}
              className="bg-blue-600 px-8 py-3 rounded-xl text-lg font-bold hover:bg-blue-700 shadow-lg transition"
            >
              Register Card →
            </button>

            {/* Accept Payment - Smart Single Button */}
            <div className="bg-gray-800 rounded-xl p-4 shadow-lg">
              <h3 className="text-center text-white font-semibold mb-3">Accept Payment</h3>
              <button
                onClick={handleSmartPayment}
                disabled={reading}
                className={`${
                  reading ? "bg-gray-600 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                } w-full px-4 py-3 rounded-lg text-base font-bold shadow-lg transition flex items-center justify-center gap-3`}
              >
                {reading ? "Reading..." : hasNFC ? "Tap Card" : "Enter Card Details"}
              </button>
              
              {/* Toggle option - small text link */}
              <button
                onClick={() => hasNFC ? setShowManualPayment(true) : handleReadNfc()}
                className="w-full mt-2 text-gray-400 hover:text-blue-400 text-xs transition"
              >
                {hasNFC ? "Or enter details manually →" : "Or scan with NFC →"}
              </button>
            </div>

            {/* Top Up Card - Smart Single Button */}
            <div className="bg-gray-800 rounded-xl p-4 shadow-lg">
              <h3 className="text-center text-white font-semibold mb-3">Top Up Card</h3>
              <button
                onClick={handleSmartTopup}
                className="bg-purple-600 hover:bg-purple-700 w-full px-4 py-3 rounded-lg text-base font-bold shadow-lg transition flex items-center justify-center gap-3"
              >
                {hasNFC ? "Tap Card" : "Enter Card Details"}
              </button>
              
              {/* Toggle option */}
              <button
                onClick={() => hasNFC ? setShowManualTopup(true) : handleTopUp()}
                className="w-full mt-2 text-gray-400 hover:text-purple-400 text-xs transition"
              >
                {hasNFC ? "Or enter ID manually →" : "Or scan with NFC →"}
              </button>
            </div>

            {/* Transaction History */}
            <div className="bg-gray-800 rounded-xl p-4 shadow-lg">
              <h3 className="text-center text-white font-semibold mb-3">Transaction History</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate("/merchant-transaction")}
                  className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-semibold transition"
                >
                  My Sales
                </button>
                <button
                  onClick={() => navigate("/card-transaction")}
                  className="bg-indigo-700 hover:bg-indigo-800 px-4 py-2 rounded-lg text-sm font-semibold transition"
                >
                  Card History
                </button>
              </div>
            </div>

            {/* Device Info Badge */}
            <div className="text-center text-xs text-gray-500 mt-2">
              {hasNFC ? "📱 NFC Available" : "⌨️ Manual Entry Mode"}
            </div>
          </div>
        )}

        {/* Manual Payment Entry View */}
        {selectedWallet && showManualPayment && (
          <div className="mt-10 w-full max-w-md bg-gray-800 rounded-2xl p-6 shadow-lg">
            <button
              onClick={() => {
                setShowManualPayment(false);
                setManualCardData("");
                setManualError("");
              }}
              className="text-blue-400 hover:text-blue-300 text-sm mb-4"
            >
              ← Back
            </button>

            <h3 className="text-xl font-bold text-white mb-4">
              💰 Accept Payment - Manual Entry
            </h3>

            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">
                Card Data (cardId|share2)
              </label>
              <textarea
                value={manualCardData}
                onChange={(e) => {
                  setManualCardData(e.target.value);
                  setManualError("");
                }}
                placeholder="1762075453759|CAIbqlTmL5/IZL7yW5bfHQ..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={4}
              />
              <p className="text-gray-500 text-xs mt-1">
                Paste the full card data from NFC
              </p>
            </div>

            {manualError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg">
                <p className="text-red-200 text-sm">⚠️ {manualError}</p>
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={handleManualPaymentEntry}
                disabled={!manualCardData.trim()}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 py-3 rounded-xl font-bold shadow-lg transition"
              >
                Continue to Enter Amount
              </button>

              <button
                onClick={handlePasteCardData}
                className="w-full bg-gray-700 hover:bg-gray-600 py-2 rounded-lg font-semibold transition text-sm"
              >
                📋 Paste from Clipboard
              </button>
            </div>

            <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-200 text-xs mb-1 font-semibold">
                Example format:
              </p>
              <p className="text-blue-200 text-xs font-mono break-all">
                1762075453759|CAIbqlTmL5...
              </p>
            </div>
          </div>
        )}

        {/* Manual Topup Entry View */}
        {selectedWallet && showManualTopup && (
          <div className="mt-10 w-full max-w-md bg-gray-800 rounded-2xl p-6 shadow-lg">
            <button
              onClick={() => {
                setShowManualTopup(false);
                setManualCardData("");
                setManualError("");
                setLoading(false);
              }}
              className="text-blue-400 hover:text-blue-300 text-sm mb-4"
            >
              ← Back
            </button>

            <h3 className="text-xl font-bold text-white mb-4">
              ⬆️ Top Up Card - Manual Entry
            </h3>

            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">
                Card ID or Card Data
              </label>
              <input
                type="text"
                value={manualCardData}
                onChange={(e) => {
                  setManualCardData(e.target.value);
                  setManualError("");
                }}
                placeholder="1762075453759 or paste full card data"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-gray-500 text-xs mt-1">
                Enter just the Card ID or paste the full card data
              </p>
            </div>

            {manualError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg">
                <p className="text-red-200 text-sm">⚠️ {manualError}</p>
              </div>
            )}

            {loading && (
              <div className="mb-4 p-3 bg-blue-500/20 border border-blue-500 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                  <p className="text-blue-200 text-sm">Fetching card details...</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={handleManualTopupEntry}
                disabled={!manualCardData.trim() || loading}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 py-3 rounded-xl font-bold shadow-lg transition"
              >
                {loading ? "Loading..." : "Continue to Top Up"}
              </button>

              <button
                onClick={handlePasteCardData}
                disabled={loading}
                className="w-full bg-gray-700 hover:bg-gray-600 py-2 rounded-lg font-semibold transition text-sm disabled:bg-gray-600"
              >
                📋 Paste from Clipboard
              </button>
            </div>

            <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-200 text-xs mb-2 font-semibold">
                💡 You can paste your full card data:
              </p>
              <p className="text-blue-200 text-xs font-mono break-all mb-2">
                1762075453759|CAIbqlTmL5...
              </p>
              <p className="text-blue-200 text-xs">
                We'll automatically extract the Card ID and fetch the card address for you!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
